/**
 * Abas de acompanhamento da ficha: Pendências (da Central, com resolver e
 * atribuir), Observações (da planilha e comentários do histórico) e Linha do
 * tempo. No ultrawide, Pendências e Atividade recente ficam no trilho à
 * direita, sempre à vista.
 */
import React, { useEffect, useState } from "react";
import { FiAlertTriangle, FiClock } from "react-icons/fi";

import ConfirmDialog from "../../../../components/ConfirmDialog";
import { useAuth } from "../../../../context/AuthContext";
import { agirPendencia, listarResponsaveis } from "../../../../services/centralProjetos";
import { formatarDataLocal } from "../../../../utils/dinheiro";

const ORIGEM = { ia: "sugerida pela IA", regra: "regra automática", manual: "criada à mão" };
const TIPOS_DA_EQUIPE = new Set(["aguardando_equipe", "sem_resposta"]);

function quando(iso) {
  if (!iso) return "";
  return iso.length <= 10 ? formatarDataLocal(iso) : new Date(iso).toLocaleDateString("pt-BR");
}

function Pendencia({ pendencia, podeTratar, responsaveis, aoMudar, compacta }) {
  const { request } = useAuth();
  const [confirmar, setConfirmar] = useState(false);
  const daEquipe = TIPOS_DA_EQUIPE.has(pendencia.tipo);
  return (
    <div className="cl-pend">
      <span className={`cl-pend-i ${daEquipe ? "equipe" : "aviso"}`} aria-hidden="true">
        {daEquipe ? <FiAlertTriangle size={compacta ? 15 : 17} /> : <FiClock size={compacta ? 15 : 17} />}
      </span>
      <div style={{ minWidth: 0 }}>
        <div className="cl-pend-t">{pendencia.descricao}</div>
        <div className="cl-pend-m">
          <span className={`cl-pill sm ${daEquipe ? "p-bad" : "p-warn"}`}><span>{pendencia.tipo_rotulo}</span></span>
          {!compacta && <span className="cl-pill sm p-neu"><span>{ORIGEM[pendencia.origem] || pendencia.origem}</span></span>}
          {pendencia.ambiente && <span>{pendencia.ambiente}</span>}
          <span>criada em {quando(pendencia.criada_em)}</span>
          {pendencia.atribuida_a && <span>· com {pendencia.atribuida_a.nome}</span>}
        </div>
        {podeTratar && !compacta && (
          <div className="cl-pend-a">
            <button type="button" className="cl-btn sm" onClick={() => setConfirmar(true)}>Marcar como resolvida</button>
            <select aria-label="Atribuir a" value={pendencia.atribuida_a?.id || ""}
              onChange={(e) => {
                agirPendencia(request, pendencia.id, "atribuir", { usuario_id: e.target.value || null })
                  // A falha já aparece em toast (wrapper `request`); a lista só não recarrega.
                  .then(aoMudar, () => undefined);
              }}>
              <option value="">Atribuir a…</option>
              {responsaveis.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
            </select>
          </div>
        )}
      </div>
      <ConfirmDialog
        open={confirmar}
        titulo="Marcar a pendência como resolvida?"
        descricao={pendencia.descricao}
        confirmarLabel="Resolver"
        tone="primary"
        onConfirmar={async () => {
          await agirPendencia(request, pendencia.id, "fechar", { estado: "resolvida" });
          setConfirmar(false);
          aoMudar();
        }}
        onCancelar={() => setConfirmar(false)}
      />
    </div>
  );
}

function useResponsaveis(ativo) {
  const { request } = useAuth();
  const [lista, setLista] = useState([]);
  useEffect(() => {
    if (!ativo) return;
    listarResponsaveis(request)
      .then((r) => setLista(Array.isArray(r) ? r : r?.responsaveis || []))
      .catch(() => setLista([]));
  }, [ativo, request]);
  return lista;
}

export function ListaDePendencias({ pendencias, podeTratar, aoMudar, compacta = false }) {
  const responsaveis = useResponsaveis(podeTratar && !compacta);
  if (!pendencias.length) return <div className="cl-vazio">Nenhuma pendência aberta.</div>;
  return pendencias.map((p) => (
    <Pendencia key={p.id} pendencia={p} podeTratar={podeTratar} responsaveis={responsaveis} aoMudar={aoMudar} compacta={compacta} />
  ));
}

export function Observacoes({ observacoes, comentarios }) {
  if (!observacoes.length && !comentarios.length) {
    return <div className="cl-vazio">Nenhuma observação na planilha e nenhum comentário no histórico.</div>;
  }
  return (
    <>
      {comentarios.length > 0 && (
        <>
          <div className="cl-sec-t"><span className="cl-eyebrow">Comentários do histórico</span></div>
          {comentarios.map((c) => (
            <div key={c.id} className="cl-obs">
              <span className="cl-ped">{c.rotulo || c.chave}</span>
              <span className="cl-obs-t">{c.texto}</span>
              <span className="cl-obs-w">{c.autor} · {new Date(c.criado_em).toLocaleDateString("pt-BR")}{c.etapa ? ` · ${c.etapa}` : ""}</span>
            </div>
          ))}
        </>
      )}
      {observacoes.length > 0 && (
        <>
          <div className="cl-sec-t"><span className="cl-eyebrow">Observações gerais da planilha</span></div>
          {observacoes.map((o) => (
            <div key={o.id} className="cl-obs">
              <span className="cl-ped">{o.pedido}</span>
              <span className="cl-obs-t">{o.observacao}</span>
              <span className="cl-obs-w">{o.ambiente}</span>
            </div>
          ))}
        </>
      )}
    </>
  );
}

export function LinhaDoTempo({ eventos, limite }) {
  const lista = limite ? eventos.slice(0, limite) : eventos;
  if (!lista.length) return <div className="cl-vazio">Sem datas na planilha para montar a linha do tempo.</div>;
  return (
    <div className="cl-feed">
      {lista.map((e) => (
        <div key={e.id} className="cl-fi" style={{ "--fc": e.cor }}>
          <div className="cl-fi-d">{e.dataLabel}</div>
          <div className="cl-fi-t">{e.titulo}</div>
          {e.detalhe && <div className="cl-fi-s">{e.detalhe}</div>}
        </div>
      ))}
    </div>
  );
}

const ATIVIDADE_NO_TRILHO = 8;

/** Trilho do ultrawide: pendências abertas e atividade recente sempre à vista. */
export function Trilho({ pendencias, comCentral, eventos, podeTratar, aoMudar }) {
  return (
    <aside className="cl-rail" aria-label="Pendências e atividade">
      {comCentral && (
        <section className="cl-card">
          <div className="cl-card-h">
            <span className="cl-eyebrow">Pendências abertas</span>
            <span className={`cl-pill sm ${pendencias.length ? "p-warn" : "p-neu"}`}><span>{pendencias.length}</span></span>
          </div>
          <ListaDePendencias pendencias={pendencias} podeTratar={podeTratar} aoMudar={aoMudar} compacta />
        </section>
      )}
      <section className="cl-card">
        <div className="cl-card-h"><span className="cl-eyebrow">Atividade recente</span></div>
        <LinhaDoTempo eventos={eventos} limite={ATIVIDADE_NO_TRILHO} />
      </section>
    </aside>
  );
}
